import { blockFacetten, type Facette, type Hoehenfunktion } from './gelaende-block';

/**
 * Schraegbilder zur Einbindung in die Nachbarschaft: die elf Lagen, die bei
 * Ebenen und Rampen (bis 3 Grad) nach Grundrissfigur und Nachbarschaft
 * unterschieden werden.
 *
 * ⚠ Woher die Formen kommen: Jede ist genau nach der Beschreibung gebaut, die
 * in derselben Tabelle steht („herausragend, allseitig abfallend, rundlich“
 * usw.). Es ist eine Zeichnung dieser Saetze, keine Vermessung - der gezeigte
 * Ausschnitt ist immer dasselbe Stueck Gelaende, damit die Lagen untereinander
 * vergleichbar bleiben.
 *
 * In der Ansicht laeuft „laengs“ nach vorn-links, „quer“ nach vorn-rechts. Die
 * offene Seite einer Lage zeigt deshalb immer nach vorn.
 */

/** Groesster Hoehenunterschied im Bild. */
const A = 0.62;

/** Glockenkurve: in der Mitte 1, nach aussen abfallend. */
function glocke(t: number, breite: number): number {
	return Math.exp(-((t / breite) ** 2));
}

/** Weiche Stufe von -1 (hinten) nach +1 (vorn). */
function sanft(t: number): number {
	return Math.tanh(t * 1.9) / Math.tanh(1.9);
}

/** Anstieg, der erst hinter einer Kante beginnt. */
function abKante(t: number, kante: number): number {
	return Math.max(0, t - kante) / (1 - kante);
}

/**
 * Die Hoehenmodelle der elf Lagen. Der Schluessel ist der Listenwert, mit dem
 * die App die Lage speichert (`assets/json/ReliefformUmgebung.json`).
 */
export const EINBINDUNGS_FORMEN: Record<string, Hoehenfunktion> = {
	// allseitig eben, grosse Ausdehnung
	Weitlage: () => 0,

	// herausragend, allseitig abfallend, rundlich
	Horstlage: (l, q) => A * glocke(Math.hypot(l, q), 0.62),

	// herausragend, allseitig abfallend, laenglich
	Rueckenlage: (l, q) => A * glocke(q, 0.45) * glocke(l, 1.6),

	// hochliegend, nach drei Seiten frei und abfallend (hinten angebunden)
	Riedellage: (l, q) => A * glocke(q, 0.5) * Math.max(0, (1 - sanft(l + 0.15)) / 2) ** 0.8,

	// hochliegend, nach einer Seite abfallend
	Schulterlage: l => A * -sanft(l),

	// zwischen zwei Arealen: eine Seite ansteigend, andere abfallend -
	// dazwischen liegt das Sims flach
	Simslage: l => A * (abKante(-l, 0.35) - abKante(l, 0.35)),

	// tiefliegend, nach einer Seite ansteigend
	Fusslage: l => A * abKante(-l, 0.1),

	// eingesenkt, nach drei Seiten ansteigend, einseitig offen (nach vorn)
	Nischenlage: (l, q) => A * (0.55 * q * q + 0.55 * Math.max(0, -l) ** 2),

	// eingesenkt, rundlich, allseitig geschlossen und ansteigend
	Senkenlage: (l, q) => -A * glocke(Math.hypot(l, q), 0.62),

	// eingesenkt, laenglich, allseitig geschlossen
	Strecksenkenlage: (l, q) => -A * glocke(q, 0.38) * glocke(l, 1.15),

	// eingesenkt, langgestreckt, zweiseitig offen - also eine durchgehende
	// Rinne mit Gefaelle, anders als die geschlossene Strecksenke
	Tallage: (l, q) => -A * glocke(q, 0.38) - 0.2 * l,
};

/**
 * Umlaute im Listenwert. Die Liste fuehrt „Rückenlage“ und „Fußlage“; als
 * Schluessel im Code stehen sie ohne Sonderzeichen.
 */
const SCHLUESSEL: Record<string, string> = {
	Rückenlage: 'Rueckenlage',
	Fußlage: 'Fusslage',
};

/** Hoehenmodell zu einer Lage, oder `null` wenn die Lage unbekannt ist. */
export function einbindungsForm(lage: string): Hoehenfunktion | null {
	const schluessel = SCHLUESSEL[lage] ?? lage;
	return EINBINDUNGS_FORMEN[schluessel] ?? null;
}

/** Die Facetten des Schraegbildes zu einer Lage. */
export function einbindungFacetten(lage: string): Facette[] {
	const form = einbindungsForm(lage);
	return form ? blockFacetten(form) : [];
}
